import { toast } from 'react-toastify'

import { call, put, select, takeLatest } from 'redux-saga/effects'

import { get, set, cloneDeep, isEmpty, isEqual, filter } from 'lodash'

import {
  loadConfig,
  loadConfigSucceeded,
  loadConfigFailed,
  createConfig,
  createConfigSucceeded,
  createConfigFailed,
  updateConfig,
  updateConfigSucceeded,
  updateConfigFailed,
} from '@ordernext/networking-stadium/stadium'

import { customHistory } from '../stores'
import StadiumRemote from '../remote/stadiumRemote'
import { fetchAttendantsSaga } from './attendant'

import { clearCart } from '../actions/cart'
import { REFRESH_CONFIG, setNetworkAvailable, shouldWaitForRedirect } from '../actions/config'
import { currentMenuSelected, menusRequested, menusItemsRequested, menusSucceeded } from '../actions/menus'
import { clearPromotionsOnOrder } from '../actions/promotion'

import { getAttendants } from '../selectors/attendant'
import { getCurrentConfiguration } from '../selectors/config'
import { getPaymentType, getBuildInfo } from '../selectors/peripheral'
import {
  getCharityDescription,
  getCharityImage,
  getCharityName,
  getCharityOptions,
  getCharityOptionsEnabled,
  getCurrentMenu,
  getEmailReceiptsEnabled,
  getPosQrReceiptsEnabled,
  getRoundUpForCharityEnabled,
  getTextReceiptsEnabled,
} from '../selectors/menus'

import { deviceAuthenticated, executeDeviceSubtype, saveConfig } from '../utils/bridgeMethods'
import createDataDogLog from '../utils/datadog'
import DeviceSubtypes from '../utils/deviceSubtypes'
import Environment from '../utils/environment'
import { ToastManager } from '../utils/toastManager'
import { getAccessToken, getDeviceId, getDeviceSerial } from '../utils/deviceData'

import { MODES } from '../VNMode/Reducer'

import { getDeviceMode, getSummitMode } from '../VNMode/Selectors'
import { BRANDING_COLOR, ORG_ENV, ORG_NAME } from '../constants'
import { getPrintingEnabled } from '../selectors/printer'
import { sendDeviceConfigCFD, sendInitializeCFD } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { getHasRecoveryData } from '../VNTransactions/Selectors'
import { clearRecoveryData } from '../VNTransactions/ActionCreators'
import { getConfiguration, getGlobalConfiguration } from '../VNConfigurations/ActionCreators'
import { fetchTaxes } from '../VNTaxes/ActionCreators'
import { setOrderNumberMin, setOrderNumberMax } from '../VNInvoiceManager/ActionCreators'
import { invoiceNumberPrefix } from '../utils/orderNumbers'
import VNLoginApi from '../VNLogin/apis/VNLoginApi'
import { getDefaultTipEnabled } from '../selectors/revenueCenter'
import { isNetworkError } from '../VNUtil/Errors'
import { getQrReceiptsUrl } from '../selectors/menus'

const directTo = (method = 'push', path) => {
  if (path === undefined) return

  customHistory[method](path)
}

const formatDeviceSubtype = (deviceSubtype) => {
  if (deviceSubtype === 'kiosk') return 'Kiosk'

  return 'Concessions'
}

const configsAreEqual = (config, newParams) => {
  const propertyAssertions = [
    ['deviceSubtype', 'payload.deviceSubtype'],
    ['configuration.menuUuids', 'payload.menuUuids'],
    ['configuration.terminalId', 'payload.terminalId'],
    ['configuration.storeId', 'payload.storeId'],
    ['configuration.esKey', 'payload.esKey'],
    ['configuration.receiptPrinterUuid', 'payload.receiptPrinterUuid'],
    ['configuration.deviceMode', 'payload.deviceMode'],
  ]

  return propertyAssertions.every(([pathOne, pathTwo]) => {
    return isEqual(get(config, pathOne), get(newParams, pathTwo))
  })
}

// Syncs the isPrintController value from stadium to the bridge
const syncPrintControllerWithBridge = (response) => {
  const appDeviceConfigurations = response?.data?.appDeviceConfigurations?.[0] || response?.data?.appDeviceConfigurations

  if (appDeviceConfigurations?.isPrintController) {
    return deviceAuthenticated({ isPrintController: true })
  }

  // checks if controlledPrinterUuids is defined
  const controlledPrinters = appDeviceConfigurations?.configuration?.controlledPrinterUuids
  if (Array.isArray(controlledPrinters) && controlledPrinters.length > 0) {
    return deviceAuthenticated({ isPrintController: true })
  }

  return deviceAuthenticated({ isPrintController: false })
}

export function* reloadMenus(ignoreThrottle) {
  yield put(menusRequested({ ignoreThrottle }))
  yield put(menusItemsRequested())

  const currentConfig = yield select(getCurrentConfiguration)
  const menuUuids = get(currentConfig, 'configuration.menuUuids', [])

  if (menuUuids.length === 1) {
    yield put(currentMenuSelected(menuUuids[0]))
  }
}

export function* initialStandAndMenuSetup(menuUuids, deviceSubtype, redirect = true) {
  try {
    const paymentType = yield select((state) => getPaymentType(state))
    const isInRecoveryMode = yield select((state) => getHasRecoveryData(state))
    const result = yield call(StadiumRemote.fetchMenus, { paymentType })
    const { menus, stands } = result?.data ?? {}
    const filteredMenus = filter(menus, (menu) => menuUuids.includes(menu?.uuid))
    if (isEmpty(filteredMenus)) {
      yield call(directTo, 'push', '/revenue-center-setup')
      yield put(shouldWaitForRedirect(false))
      return
    }
    const standUuids = [...new Set(filteredMenus.map((menu) => menu.standUuid))]
    const filteredStands = filter(stands, (stand) => standUuids.includes(stand?.uuid))
    yield put(menusSucceeded({ data: { stands: filteredStands, menus: filteredMenus } }))
    yield put(menusItemsRequested())
    yield put(currentMenuSelected(menuUuids[0]))

    const currentMenu = yield select(getCurrentMenu)
    const deviceConfiguration = yield select(getCurrentConfiguration)
    const deviceId = getDeviceId()
    const serialNumber = getDeviceSerial()
    const accessToken = getAccessToken()
    const cfdMode = yield select(getDeviceMode)
    const printingEnabled = yield select(getPrintingEnabled)
    const textReceiptsEnabled = yield select(getTextReceiptsEnabled)
    const emailReceiptsEnabled = yield select(getEmailReceiptsEnabled)
    const posQrCodeReceiptsEnabled = yield select(getPosQrReceiptsEnabled)
    const defaultTipEnabled = yield select(getDefaultTipEnabled)
    const qrReceiptsUrl = yield select(getQrReceiptsUrl)

    const roundUpForCharityEnabled = yield select(getRoundUpForCharityEnabled)
    const charityOptionsEnabled = yield select(getCharityOptionsEnabled)
    const charityDescription = yield select(getCharityDescription)
    const charityOptions = yield select(getCharityOptions)
    const charityImage = yield select(getCharityImage)
    const charityName = yield select(getCharityName)

    yield put(fetchTaxes())

    // if the device is in CFD mode resend the initialization values incase they have changed.
    if (cfdMode === MODES.POS) {
      const initializationValues = {
        title: filteredStands?.[0]?.name,
        color: BRANDING_COLOR,
        deviceId,
        serialNumber,
        appConfigurationDeviceId: deviceConfiguration?.uuid,
        accessToken: accessToken,
        printingEnabled: printingEnabled,
        textReceiptsEnabled: textReceiptsEnabled,
        emailReceiptsEnabled: emailReceiptsEnabled,
        enablePosQrCodeReceipts: posQrCodeReceiptsEnabled,
        qrReceiptsUrl: qrReceiptsUrl,
        invoiceNumberPrefix: invoiceNumberPrefix(),
        charitySettings: {
          roundUpForCharityEnabled,
          charityOptionsEnabled,
          charityDescription,
          charityOptions,
          charityImage,
          charityName,
        },
        defaultTipEnabled,
      }

      initializationValues.menuImage = currentMenu?.images?.detail
      initializationValues.image = filteredStands?.[0]?.images?.detail
      sendInitializeCFD(initializationValues)
    }

    if (isInRecoveryMode) {
      yield put(shouldWaitForRedirect(false))
      yield put(clearRecoveryData())
      return
    }

    if (!redirect) return

    if (DeviceSubtypes.isKiosk(deviceSubtype)) {
      yield put(shouldWaitForRedirect(false))
    } else {
      yield call(fetchAttendantsSaga)
      const attendants = yield select(getAttendants)
      if (!redirect) {
        yield put(shouldWaitForRedirect(false))
        return
      }
      const pathname = isEmpty(attendants) ? '/concession-order' : '/user-login'
      yield call(directTo, 'push', pathname)
      yield put(shouldWaitForRedirect(false))
    }
  } catch {
    yield call(directTo, 'push', '/revenue-center-setup')
    yield put(shouldWaitForRedirect(false))
  }
}

let errorToast = undefined

export function* getConfigRequest(redirect = true, shouldReloadMenus = true) {
  const appDeviceConfiguration = yield select(getCurrentConfiguration)
  const cfdMode = yield select(getDeviceMode)
  const isSummitMode = yield select(getSummitMode)
  const serialNumber = getDeviceSerial(false) || null

  if (redirect) {
    yield put(shouldWaitForRedirect(true))
  }

  try {
    const result = yield call(StadiumRemote.getConfig)

    // when configurations is empty, attempt to login the device in case it's locked
    if (result?.data?.appDeviceConfigurations?.length === 0) {
      yield VNLoginApi().postDeviceLogin()
    }

    // migrate deviceConfigurtaion in Stadium to include serial number
    const currentConfiguration = result?.data?.appDeviceConfigurations?.[0] || {}
    if (isSummitMode && !currentConfiguration.serialNumber && currentConfiguration.uuid) {
      const updatePayload = {
        appDeviceConfigurations: [{ ...currentConfiguration, serialNumber }],
        uuid: currentConfiguration.uuid,
      }

      yield call(StadiumRemote.updateConfig, updatePayload)
    }

    let deviceSubtype = get(result, 'data.appDeviceConfigurations[0].deviceSubtype')

    if (!deviceSubtype) {
      deviceSubtype = get(result, 'data.appDeviceConfiguration.deviceSubtype')
    }

    if (!['concessions-pos', 'kiosk'].includes(deviceSubtype)) {
      yield put(loadConfigSucceeded(result))
      yield call(directTo, 'push', '/revenue-center-setup')
      if (!toast.isActive(errorToast)) {
        errorToast = ToastManager.error(
          `Expected Concessions or Kiosk device type but received: ${formatDeviceSubtype(deviceSubtype ?? 'No subtype selected')}`,
          { autoClose: false },
        )
        createDataDogLog(
          'error',
          `Expected Concessions or Kiosk device type but received: ${formatDeviceSubtype(deviceSubtype ?? 'No subtype selected')}`,
        )
      }

      return false
    }

    syncPrintControllerWithBridge(result)

    // TODO: (@westinschepper): Use this when we opt in to passing all data to the APK
    // const appDeviceConfig = get(result, 'data.appDeviceConfiguration', get(result, 'data.appDeviceConfigurations[0]', {}))
    // const flattenedAppDeviceConfig = {
    //   ...appDeviceConfig,
    //   ...appDeviceConfig.configuration,
    // }

    let max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    let min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    let configuration = get(result, 'data.appDeviceConfigurations[0].configuration', {})
    let { terminalId, storeId, esKey, prodUrl, uatUrl, host, menuUuids, deviceMode } = configuration

    if (!deviceMode) {
      createDataDogLog('info', '[T44636] Device mode not found in configuration', {
        ResultFromStadium: result,
        localStorage: appDeviceConfiguration,
      })
      deviceMode = appDeviceConfiguration?.configuration?.deviceMode ?? cfdMode
    }

    /**
     * TODO:
     * We should ask about getting this changed.
     * if there is only one configuration, Stadium returns a singular key name.
     */
    if (!max) max = get(result, 'data.appDeviceConfiguration.orderNumberEnd')
    if (!min) min = get(result, 'data.appDeviceConfiguration.orderNumberStart')
    if (!host) host = get(result, 'data.appDeviceConfiguration.configuration.host')
    if (!storeId) storeId = get(result, 'data.appDeviceConfiguration.configuration.storeId')
    if (!terminalId) terminalId = get(result, 'data.appDeviceConfiguration.configuration.terminalId')
    if (!esKey) esKey = get(result, 'data.appDeviceConfiguration.configuration.esKey')
    if (!prodUrl) prodUrl = get(result, 'data.appDeviceConfiguration.configuration.prodUrl', 'https://cs.freedompay.us/Fasta/')
    if (!uatUrl) uatUrl = get(result, 'data.appDeviceConfiguration.configuration.uatUrl', 'https://cs.uat.freedompay.com/Fasta/')

    if (isEmpty(menuUuids)) menuUuids = result?.data?.appDeviceConfigurations?.configuration?.menuUuids ?? []
    yield put(currentMenuSelected(menuUuids[0]))

    localStorage.setItem('orderNumberMin', min)
    yield put(setOrderNumberMin(min))

    localStorage.setItem('orderNumberMax', max)
    yield put(setOrderNumberMax(max))

    // FIXME: Update this when the URL's are coming back as expected from Stadium
    let url = uatUrl

    if (Environment.app.isProduction) {
      url = prodUrl
    }

    const deviceConfig = {
      storeID: storeId,
      terminalID: terminalId,
      esKey,
      host,
      url,
      deviceSubtype,
      deviceMode,
    }

    saveConfig(deviceConfig)
    sendDeviceConfigCFD(deviceConfig)

    if (!isEmpty(menuUuids) && shouldReloadMenus) {
      yield initialStandAndMenuSetup(menuUuids, deviceSubtype, redirect)
    }

    if (redirect) {
      if (isEmpty(menuUuids)) {
        yield call(directTo, 'push', '/revenue-center-setup')
        yield put(shouldWaitForRedirect(false))
      }

      // if the device is a kiosk, redirect to the attract loop
      const isKiosk = result?.data?.appDeviceConfigurations?.[0]?.deviceSubtype === 'kiosk'
      if (isKiosk) {
        yield call(directTo, 'push', '/kiosk/attract_loop')
      }
    } else if (shouldReloadMenus) {
      yield reloadMenus(true)
    }

    // Set android to concessions device subtype in order to trigger offline only mode cool down for all pos modes
    executeDeviceSubtype(DeviceSubtypes.concessions)

    yield put(loadConfigSucceeded(result))
    return result
  } catch (err) {
    yield put(loadConfigFailed(err))
    yield put(shouldWaitForRedirect(false))

    /**
     * Checking for network error to push config to the bridge
     * Since we are not able to get the config from the server we're pulling it from redux state
     * and pushing it to the bridge upon a network error
     */
    if (isNetworkError(err) && appDeviceConfiguration?.configuration) {
      const deviceSubtype = appDeviceConfiguration?.deviceSubtype
      const { terminalId, storeId, prodUrl, uatUrl, esKey, host, deviceMode } = appDeviceConfiguration.configuration

      let url = uatUrl

      if (Environment.app.isProduction) {
        url = prodUrl
      }

      const deviceConfig = {
        storeID: storeId,
        terminalID: terminalId,
        esKey,
        host,
        url,
        deviceSubtype,
        deviceMode,
      }

      if (!deviceMode) {
        createDataDogLog('error', '[T44636] Network Error. Device mode not found in configuration', {
          localStorage: appDeviceConfiguration,
        })
      }

      saveConfig(deviceConfig)
      sendDeviceConfigCFD(deviceConfig)
    }

    // mark as offline and prevent redirection
    if (isNetworkError(err)) {
      yield put(setNetworkAvailable(false))
      return
    }

    if (redirect) {
      yield call(directTo, 'push', '/revenue-center-setup')
    }
  }
}

export function* getConfig(params = {}) {
  // If the app reloads (should refresh config) or is navigated to root (device login) "redirect" flag will be true and will navigate
  // the user based on the device's fetched app device configuration

  // The stadium package puts these properties in a strange ID parameter, but our deprecated
  // methods put these properties in the payload.
  const redirect = params?.payload?.id?.redirect ?? params?.payload?.redirect
  const shouldReloadMenus = params?.payload?.id?.shouldReloadMenus ?? params?.payload?.shouldReloadMenus

  yield call(getConfigRequest, redirect, shouldReloadMenus)
}

function* refreshConfigSaga() {
  try {
    yield put(clearCart())
    yield put(clearPromotionsOnOrder())
    let result = yield call(getConfigRequest, false)

    //getConfiguration will silently fail if request doesn't succeed
    yield put(getConfiguration(ORG_NAME, ORG_ENV, 'public', 'runtime'))
    yield put(getGlobalConfiguration(ORG_ENV, 'public', 'runtime'))

    if (result) {
      ToastManager.success('Config refreshed successfully')
    }
  } catch (err) {
    ToastManager.error('Failed to refresh config', { autoClose: false })
  }
}

export function* watchGetConfig() {
  yield takeLatest(loadConfig.type, getConfig)
}

export function* watchRefreshConfigSaga() {
  yield takeLatest(REFRESH_CONFIG, refreshConfigSaga)
}

export function* _createConfig(params = {}) {
  try {
    const menuUuids = get(params, 'payload.menuUuids', [])
    const printerUuid = get(params, 'payload.receiptPrinterUuid')
    const deviceSubtype = get(params, 'payload.deviceSubtype')
    const deviceMode = get(params, 'payload.deviceMode')
    const buildInfo = yield select(getBuildInfo)

    const body = {
      appDeviceConfigurations: [
        {
          configuration: {
            menuUuids: menuUuids,
            deviceMode: deviceMode,
            receiptPrinterUuid: printerUuid,
            buildInfo: buildInfo,
          },
          deviceSubtype,
          organizationName: process.env.REACT_APP_ORG,
          venueUuid: process.env.REACT_APP_VENUE_UUID,
        },
      ],
    }

    const result = yield call(StadiumRemote.createConfig, body)

    syncPrintControllerWithBridge(result)

    const max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    const min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    localStorage.setItem('orderNumberMax', max)
    localStorage.setItem('orderNumberMin', min)

    yield put(createConfigSucceeded(result))
    yield reloadMenus()
    yield call(getConfigRequest, undefined, false)
  } catch (err) {
    yield put(createConfigFailed(err))
  }
}

export function* watchCreateConfig() {
  yield takeLatest(createConfig.type, _createConfig)
}

export function* _updateConfig(params = {}) {
  try {
    const menuUuids = get(params, 'payload.menuUuids')
    const terminalId = get(params, 'payload.terminalId')
    const storeId = get(params, 'payload.storeId')
    const esKey = get(params, 'payload.esKey')
    const deviceSubtype = get(params, 'payload.deviceSubtype')
    const deviceMode = get(params, 'payload.deviceMode')
    const printerUuid = get(params, 'payload.receiptPrinterUuid')
    const currentConfiguration = yield select((state) => get(state, 'config'))
    const clone = cloneDeep(currentConfiguration)
    const isSummit = select(getSummitMode)
    const buildInfo = yield select(getBuildInfo)

    if (!isEmpty(menuUuids)) {
      set(clone, 'configuration.menuUuids', menuUuids)
    }

    if (deviceSubtype) {
      set(clone, 'deviceSubtype', deviceSubtype)
    }

    if (deviceMode) {
      set(clone, 'configuration.deviceMode', deviceMode)
    }

    if (terminalId && storeId && esKey) {
      set(clone, 'configuration.terminalId', terminalId)
      set(clone, 'configuration.storeId', storeId)
      set(clone, 'configuration.esKey', esKey)
    }

    if (printerUuid) {
      set(clone, 'configuration.receiptPrinterUuid', printerUuid)
    }

    if (!isEmpty(buildInfo)) {
      set(clone, 'configuration.buildInfo', buildInfo)
    }

    delete clone.hasConfig
    delete clone.menuUuid
    delete clone.remote

    // when in summit mode, only send the serial number to stadium
    const serialNumber = getDeviceSerial()
    if (isSummit && serialNumber) {
      if (clone.deviceId) {
        delete clone.deviceId
      }
      if (!clone.serialNumber) {
        clone.serialNumber = serialNumber
      }
    }

    const body = {
      appDeviceConfigurations: [clone],
      uuid: get(currentConfiguration, 'uuid'),
    }

    if (configsAreEqual(currentConfiguration, params)) {
      // This success action just merges, so it is safe to pass an empty object.
      yield put(updateConfigSucceeded({}))
      return
    }

    const result = yield call(StadiumRemote.updateConfig, body)

    syncPrintControllerWithBridge(result)

    const max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    const min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    localStorage.setItem('orderNumberMax', max)
    localStorage.setItem('orderNumberMin', min)

    yield put(updateConfigSucceeded(result))
  } catch (err) {
    yield put(updateConfigFailed(err))
    if (err?.response?.data?.errorMessage?.includes('Validation failed')) {
      yield call(getConfigRequest, false, false)
    }
  }
}

export function* watchUpdateConfig() {
  yield takeLatest(updateConfig.type, _updateConfig)
}
